import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Message from "../components/LoadingError/Error";
import Loading from "../components/LoadingError/Loading";
import { register, googleLogin } from "../Redux/Actions/userActions";
import Header from "./../components/Header";
import "./login.css";

const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Register = () => {
  const location     = useLocation();
  const params       = new URLSearchParams(location.search);
  const rawRedirect  = params.get("redirect") || "/";
  const redirect     = rawRedirect.startsWith("/") ? rawRedirect : `/${rawRedirect}`;
  const emailFromUrl = params.get("email") || "";

  const [username,   setUsername]   = useState("");
  const [firstName,  setFirstName]  = useState("");
  const [lastName,   setLastName]   = useState("");
  const [email,      setEmail]      = useState(emailFromUrl);
  const [password,   setPassword]   = useState("");
  const [localError, setLocalError] = useState("");
  const nonceRef = useRef(crypto.randomUUID());

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userRegister = useSelector((state) => state.userRegister);
  const { error: registerError, loading: registerLoading, userInfo: registerUserInfo } = userRegister;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo: loginUserInfo } = userLogin;

  const userGoogleLogin = useSelector((state) => state.userGoogleLogin);
  const { error: googleError, loading: googleLoading } = userGoogleLogin;

  const userInfo     = registerUserInfo || loginUserInfo;
  const isLoading    = registerLoading  || googleLoading;
  const displayError = localError || registerError || googleError;

  const handleGoogleResponse = useCallback(
    (response) => {
      setLocalError("");
      dispatch(googleLogin(response.credential, nonceRef.current));
    },
    [dispatch]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!GOOGLE_CLIENT_ID) return;

    let script = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]'
    );

    const initGoogle = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id:   GOOGLE_CLIENT_ID,
        callback:    handleGoogleResponse,
        nonce:       nonceRef.current,
        auto_select: false,
      });
      const btn = document.getElementById("googleSignUpButton");
      if (btn) {
        window.google.accounts.id.renderButton(btn, {
          theme: "outline",
          size:  "medium",
          width: 332,
          text:  "signup_with",
        });
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.src   = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      script.onload = initGoogle;
    } else if (window.google) {
      initGoogle();
    } else {
      script.addEventListener("load", initGoogle);
      return () => script.removeEventListener("load", initGoogle);
    }

    return () => {
      const btn = document.getElementById("googleSignUpButton");
      if (btn) btn.innerHTML = "";
    };
  }, [handleGoogleResponse]);

  useEffect(() => {
    if (userInfo) navigate(redirect);
  }, [userInfo, navigate, redirect]);

  const submitHandler = (e) => {
    e.preventDefault();
    setLocalError("");

    if (!username.trim() || !firstName.trim() || !lastName.trim()) {
      setLocalError("Please enter a username, first name, and last name.");
      return;
    }
    if (!email.trim()) {
      setLocalError("Please enter your email address.");
      return;
    }
    if (password.length < 6) {
      setLocalError("Password must be at least 6 characters.");
      return;
    }

    dispatch(
      register(
        username.trim(),
        `${firstName.trim()} ${lastName.trim()}`,
        email.trim(),
        password
      )
    );
  };

  return (
    <>
      <Header />
      <div className="login-center">

        <div className="login-message-slot">
          {displayError && <Message variant="alert-danger">{displayError}</Message>}
          {isLoading    && <Loading />}
        </div>

        <form
          className="Login"
          onSubmit={submitHandler}
          style={{ opacity: isLoading ? 0.6 : 1, transition: "opacity 0.2s ease" }}
        >
          <p className="login-card-title">Create your account</p>

          {/* ── Google ── */}
          <div className="google-btn-wrapper">
            <div
              id="googleSignUpButton"
              style={{
                opacity:       isLoading ? 0.4 : 1,
                pointerEvents: isLoading ? "none" : "auto",
                transition:    "opacity 0.2s ease",
              }}
            />
          </div>

          <div className="login-divider">or sign up with email</div>

          {/* ── Fields ── */}
          <input
            type="text"
            placeholder="Username"
            value={username}
            disabled={isLoading}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            type="text"
            placeholder="First Name"
            value={firstName}
            disabled={isLoading}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Last Name"
            value={lastName}
            disabled={isLoading}
            onChange={(e) => setLastName(e.target.value)}
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            disabled={isLoading}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password (min 6 characters)"
            value={password}
            disabled={isLoading}
            onChange={(e) => setPassword(e.target.value)}
          />

          {/* ── Action buttons ── */}
          <div className="login-actions">
            {/* 1. Primary: Register */}
            <button type="submit" className="btn-primary-login" disabled={isLoading}>
              {isLoading ? "Creating account…" : "Create Account"}
            </button>

            {/* 2. Back to login (outlined, neutral) */}
            <p className="login-footer-link">
              Already have an account?{" "}
              <Link to={redirect ? `/login?redirect=${redirect}` : "/login"}>
                Sign In
              </Link>
            </p>
          </div>
        </form>
      </div>
    </>
  );
};

export default Register;